import { currentFeatures, oids } from "@/configs/Settings";
import { useClinicalRegisterContext } from "@/contexts/ClinicalRegisterContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { EditAppointment } from "@/services/fhir";
import { patchAppointmentStatusAndTriggerPreConsultation } from "@/services/fhir/appointment/patchAppointmentStatusAndTriggerPreConsultation";
import {
  buildPreConsultationTriggerContextFromAppointment,
  buildScheduleManagerRequesterReference,
  buildTriggerInputFromPreConsultationContext,
  triggerPreConsultationAutomation,
} from "@/services/crm/triggerClinicalAutomation";
import { GetPaymentNotice } from "@/services/fhir/paymentnotice/GetPaymentNotice";
import { PutPaymentNotice } from "@/services/fhir/paymentnotice/PutPaymentNotice";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { useDeleteEvent } from "@/services/google/DeleteEvent";
import { usePostEvent } from "@/services/google/PostEvent";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import {
  buildCobrancaConsultaNotification,
  buildCobrancaConsultaVetNotification,
} from "@/services/notification/notificationHelpers";
import { notificationService } from "@/services/notification/PostNotification";
import { useFormatter } from "@/utils/useFormatter";
import { useKeepPaymentModalOpenAfterCharge } from "@/hooks/useKeepPaymentModalOpenAfterCharge";
import {
  isChargeNotificationSentOnAppointment,
  markAppointmentChargeNotificationSent,
} from "@/utils/chargeNotificationSent";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFhirData } from "@/utils/useFhirData";

interface UseAppointmentActionsResult {
  startServiceLoading: boolean;
  handleStartService: (appointment: any, proceed?: boolean) => Promise<void>;
  handleAddToCalendar: (appointment: any) => Promise<void>;
  handleRemoveFromCalendar: (appointment: any) => Promise<void>;
  handleViewRecords: (appointment: any) => Promise<void>;
  fetchRelated: () => Promise<void>;
  loadingCalendar: boolean;
  setVerifyDialog: (verifyDialog: boolean) => void;
  verifyDialog: boolean;
  warningAction: string;
  showPaymentWarning: boolean;
  setShowPaymentWarning: (showPaymentWarning: boolean) => void;
  related: any;
  fetchPayment: () => Promise<void>;
  payment: any;
  isPaid: boolean;
  paymentModalOpen: boolean;
  setPaymentModalOpen: (paymentModalOpen: boolean) => void;
  loadingSendCharge: boolean;
  setLoadingSendCharge: (loadingSendCharge: boolean) => void;
  errorSendCharge: string | undefined;
  setErrorSendCharge: (errorSendCharge: string | undefined) => void;
  loadingConfirmPayment: boolean;
  setLoadingConfirmPayment: (loadingConfirmPayment: boolean) => void;
  handleSendCharge: () => Promise<void>;
  handleConfirmPayment: () => Promise<void>;
  loadingConfirmProposed: boolean;
  handleConfirmProposedAppointment: () => Promise<string | undefined>;
}

interface UseAppointmentActionsProps {
  appointment?: any;
  setRefetch?: any;
}

/**
 * Hook para gerenciar filtros administrativos
 * Controla seleção de localização e profissional
 * Integra com LocationContext e PractitionerRoleContext para dados
 *
 * @param props.defaultLocation - ID da localização inicial (opcional)
 * @param props.defaultPractitioner - ID do profissional inicial (opcional)
 * @param props.onChange - Callback executado quando os filtros mudam (opcional)
 *
 * @example
 * ```tsx
 * // Uso básico
 * const { selectedLocation, selectedPractitioner, handleLocationChange } = useAppointmentActions();
 *
 * // Com valores default e callback
 * const filters = useAppointmentActions({
 *   defaultLocation: 'loc1',
 *   onChange: (filters) => console.log('Filtros atualizados:', filters)
 * });
 * ```
 */
export const useAppointmentActions = (
  props?: UseAppointmentActionsProps,
): UseAppointmentActionsResult => {
  const navigate = useNavigate();
  const { organization } = useOrganizationContext();
  const { verifyEncounter } = useClinicalRegisterContext();
  const { accessTokenGoogle, user } = useSessionContext();
  const [verifyDialog, setVerifyDialog] = useState<boolean>(false);
  const [warningAction, setWarningAction] = useState<string>("");
  const [showPaymentWarning, setShowPaymentWarning] = useState<boolean>(false);
  const [related, setRelated] = useState<any>([]);
  const [payment, setPayment] = useState<any>([]);
  const [isPaid, setIsPaid] = useState<boolean>(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [loadingSendCharge, setLoadingSendCharge] = useState(false);
  const [errorSendCharge, setErrorSendCharge] = useState<string | undefined>(
    undefined,
  );
  const [loadingConfirmPayment, setLoadingConfirmPayment] = useState(false);
  // Estado para controlar o loading da confirmação de appointment proposed
  const [loadingConfirmProposed, setLoadingConfirmProposed] = useState(false);
  const keepOpenAfterCharge = useKeepPaymentModalOpenAfterCharge();

  async function fetchRelated() {
    const patientId = useFhirData.findParticipant(
      props?.appointment?.originalData?.participant,
      "Patient",
    )?.reference;

    if (!currentFeatures?.defaultApp || !patientId) return;

    const relatedResponse = await GetRelatedPerson({
      patient: currentFeatures.defaultApp
        ? `${oids.cpf}-${patientId?.split("-")?.[1]}`
        : patientId?.split("/")?.[1],
    }).then((response) => response?.data?.entry);
    const verifyRelated = relatedResponse?.filter(
      (el: any) =>
        el?.resource?.identifier &&
        !el?.resource?.active &&
        el?.resource?.period?.start,
    );
    if (verifyRelated?.length) setRelated(verifyRelated);
    else setRelated([]);
    return verifyRelated;
  }

  const checkAppointmentPayment = () => {
    // Verificação baseada no status do atendimento
    // Se for 'pending' não foi pago, se for 'booked' foi pago
    return props?.appointment?.originalData?.status === "booked";
  };

  const { postEvent } = usePostEvent();
  const { deleteEvent } = useDeleteEvent();

  // Obtendo dados dos contexto

  const [startServiceLoading, setStartServiceLoading] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  const fetchPayment = async () => {
    if (props?.appointment?.originalData?.id) {
      // Encontra o participant que é o paciente
      const patientParticipant =
        props?.appointment?.originalData?.participant?.find((p: any) =>
          p.actor?.reference?.startsWith("Patient/"),
        );

      // Encontra o slot no appointment
      const slotReference =
        props?.appointment?.originalData?.slot?.[0]?.reference;

      if (patientParticipant?.actor?.reference && slotReference) {
        const filter = `status=active&_sort=-_lastUpdated&request=${slotReference}&response=${patientParticipant.actor.reference}&_count=1`;
        const response = await GetPaymentNotice({ filter });
        const paymentData = response?.data?.entry || [];
        setPayment(paymentData);

        // Verifica se existe paymentStatus no PaymentNotice
        const hasPaymentStatus = paymentData?.[0]?.resource?.paymentStatus;
        setIsPaid(!!hasPaymentStatus);

        // Retorna os dados para uso imediato
        return paymentData;
      }
    }
    return [];
  };

  const handleRemoveFromCalendar = async (appointment: any) => {
    try {
      setLoadingCalendar(true);
      const googleCalendarExtension =
        appointment?.originalData?.extension?.find((ext: any) =>
          ext.url?.includes("googleCalendarId"),
        );
      if (googleCalendarExtension?.valueString) {
        try {
          await deleteEvent({
            eventId: googleCalendarExtension.valueString,
            accessTokenGoogle: accessTokenGoogle || "",
            refreshToken: user?.refreshTokenGoogle || "",
          });
        } catch (deleteError) {
          console.error("Erro ao excluir evento:", deleteError);
        }
      }
      // Remove apenas a extensão googleCalendarId
      let newExtensions: any[] = [];
      if (Array.isArray(appointment?.originalData?.extension)) {
        newExtensions = appointment.originalData.extension.filter(
          (ext: any) =>
            ext.url !==
            "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId",
        );
      }
      await EditAppointment({
        ...appointment.originalData,
        extension: newExtensions,
      });

      props?.setRefetch?.((prev: any) => !prev);
      toast.success("Removido do Google Calendar");
    } catch (error) {
      console.error("Erro ao remover do Google Calendar:", error);
      toast.error("Erro ao remover do Google Calendar");
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleAddToCalendar = async (appointment: any) => {
    const hasCalendarId = appointment?.originalData?.extension?.some(
      (ext: any) => ext.url.includes("googleCalendarId") && ext.valueString,
    );

    if (hasCalendarId) {
      toast.warning("Consulta já está no Google Calendar");
      return;
    }

    try {
      setLoadingCalendar(true);
      if (!accessTokenGoogle) {
        toast.warning("Token do Google Calendar não encontrado");
        return;
      }

      // Detecta se é atendimento a domicílio pela extension home-visit-address
      const homeVisitExt = appointment?.originalData?.extension?.find(
        (ext: any) => ext.url?.includes("home-visit-address"),
      );
      const homeVisitAddress: fhir5.Address | undefined =
        homeVisitExt?.valueAddress;

      // Formata o endereço domiciliar como string para o Google Calendar
      // Exemplo: "Rua das Flores, 123 - Bairro Centro - São Paulo - SP, 01310-100"
      const formatHomeAddress = (addr: fhir5.Address): string => {
        const parts: string[] = [];
        if (addr.line?.length) parts.push(addr.line.join(", "));
        if (addr.district) parts.push(addr.district);
        const cityState = [addr.city, addr.state].filter(Boolean).join(" - ");
        if (cityState) parts.push(cityState);
        if (addr.postalCode) {
          const cep = addr.postalCode.replace(/\D/g, "");
          parts.push(
            cep.length === 8
              ? `${cep.slice(0, 5)}-${cep.slice(5)}`
              : addr.postalCode,
          );
        }
        return parts.join(" - ");
      };

      // Prioridade: endereço domiciliar > link virtual > localização da agenda
      const eventLocation = homeVisitAddress
        ? formatHomeAddress(homeVisitAddress)
        : appointment.virtualService || appointment.location;

      const eventData = {
        summary: `Consulta: ${appointment.healthcare}`,
        description: `Paciente: ${appointment.patientName}`,
        location: eventLocation,
        start: {
          dateTime: appointment.startTime || new Date().toISOString(),
          timeZone: "America/Sao_Paulo",
        },
        end: {
          dateTime: appointment.endTime || new Date().toISOString(),
          timeZone: "America/Sao_Paulo",
        },
      };

      const eventResponse = await postEvent({
        eventData,
        accessTokenGoogle: accessTokenGoogle,
        refreshToken: user.refreshTokenGoogle || "",
      });

      if (eventResponse?.id) {
        // Usa as extensions do recurso FHIR original (originalData) para não perder
        // nenhuma extension existente (ex: home-visit-address, notification-sent, etc.)
        const existingExtensions: any[] = Array.isArray(
          appointment?.originalData?.extension,
        )
          ? appointment.originalData.extension.filter(
              (ext: any) =>
                ext.url !==
                "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId",
            )
          : [];

        // Adiciona a nova extensão no início, mantendo todas as demais
        const newExtensions = [
          {
            url: "https://fluxmed.com.br/fhir/StructureDefinition/googleCalendarId",
            valueString: eventResponse.id,
          },
          ...existingExtensions,
        ];

        await EditAppointment({
          ...appointment.originalData,
          extension: newExtensions,
        });
        props?.setRefetch?.((prev: boolean) => !prev);
        toast.success("Adicionado ao Google Calendar");
      }
    } catch (error) {
      console.error("Erro ao adicionar ao Google Calendar:", error);
      toast.error("Erro ao adicionar ao Google Calendar");
    } finally {
      setLoadingCalendar(false);
    }
  };

  const handleStartService = async (appointment: any, proceed?: boolean) => {
    setStartServiceLoading(true);
    try {
      {
        const verifyRelated = await fetchRelated();
        console.log("verifyRelated", verifyRelated);

        if (verifyRelated?.length) {
          setVerifyDialog(true);
        } else {
          // Verificar se o atendimento foi pago
          const isPaid = checkAppointmentPayment();

          if (!isPaid && !proceed) {
            // Se não foi pago, mostrar alerta
            setWarningAction("startService");
            setShowPaymentWarning(true);
          } else {
            // Se foi pago, continuar normalmente
            await verifyEncounter(appointment?.originalData);
          }
        }
      }
    } catch (error) {
      console.error("Erro ao iniciar atendimento:", error);
      toast.error("Erro ao iniciar atendimento");
    } finally {
      setStartServiceLoading(false);
    }
  };

  const handleViewRecords = async (appointment: any) => {
    const patientId = currentFeatures.defaultApp
      ? `${appointment?.participant?.[0]?.actor?.reference?.split("-")[1]}`
      : appointment?.participant?.[0]?.actor?.reference?.split("/")[1];

    if (patientId) {
      navigate("/summary", {
        state: {
          patientId,
          appointment: appointment,
        },
      });
    } else {
      toast.warn("Erro ao acessar registros");
    }
  };

  const handleSendCharge = async () => {
    setLoadingSendCharge(true);
    setErrorSendCharge(undefined);
    try {
      const paymentLink = await marketplaceService.sendPaymentMessage({
        slotReference:
          props?.appointment?.originalData?.slot?.[0]?.reference || "",
        patientName: props?.appointment?.patientName || "",
        healthcareDisplay: props?.appointment?.healthcare || "",
        practitionerName: props?.appointment?.practitioner || "",
        phoneNumber: props?.appointment?.patientPhone || "",
      });

      // Se não for defaultApp (modo vet), buscar informações do tutor
      if (!currentFeatures.defaultApp) {
        // Encontra o participant que é o paciente (pet)
        const patientParticipant =
          props?.appointment?.originalData?.participant?.find((p: any) =>
            p?.actor?.reference?.startsWith("Patient/"),
          );

        // Extrai o ID do paciente (pet)
        const patientId = patientParticipant?.actor?.reference?.split("/")?.[1];

        if (!patientId) {
          throw new Error("ID do paciente não encontrado");
        }

        // Busca o tutor relacionado ao pet
        const relatedPersonResponse = await GetRelatedPerson({
          patient: patientId,
          vetRelated: true, // Indica que é para sistema vet
        });

        // Extrai dados do tutor
        let tutorCpf = "";
        let tutorName = "";

        if (
          relatedPersonResponse &&
          relatedPersonResponse?.data?.entry?.length > 0
        ) {
          const relatedPerson = relatedPersonResponse.data.entry[0]?.resource;

          // Extrai CPF do tutor
          const tutorIdentifier = relatedPerson?.identifier?.[0]?.value || "";
          tutorCpf = tutorIdentifier;

          // Extrai nome do tutor
          const tutorNameObj = relatedPerson?.name?.find(
            (n: any) => n.use === "official" || n.use === "usual",
          );
          tutorName = tutorNameObj?.text || "";
        }

        if (!tutorCpf) {
          throw new Error("CPF do tutor não encontrado");
        }

        // Nome do pet (que está em patientName)
        const petName = props?.appointment?.patientName || "";

        // Nome da clínica/organização
        const hospitalName = organization?.name || "";

        // Envia notificação com template específico para vet
        // Usa 'as any' porque o tipo INotificationParameters não inclui 'pet' e 'hospital'
        // mas o template FluxvetPagamentoConsulta requer esses parâmetros
        await notificationService.sendNotification(
          buildCobrancaConsultaVetNotification({
            cpf: tutorCpf,
            nomeTutor: useFormatter.formatNameDisplay(tutorName) || "",
            nomePet: petName || "",
            nomeProfissional: props?.appointment?.practitioner || "",
            nomeClinica: hospitalName || "",
            linkPagamento: paymentLink,
            organizationId: organization?.id,
          }),
          { organizationId: organization?.id },
        );
      } else {
        // Comportamento padrão para defaultApp
        // CPF a partir do participant Patient em appointment.originalData.participant
        // Formato esperado: "Patient/<OID>-<CPF>". Fallback para subject.reference
        const patientParticipant =
          props?.appointment?.originalData?.participant?.find((p: any) =>
            p?.actor?.reference?.startsWith("Patient/"),
          );
        const cpfFromParticipant = currentFeatures.defaultApp
          ? patientParticipant?.actor?.reference
              ?.split("/")?.[1]
              ?.split("-")?.[1] || ""
          : patientParticipant?.actor?.reference?.split("/")?.[1] || "";
        const subjectRef =
          props?.appointment?.subject?.reference ||
          props?.appointment?.originalData?.subject?.reference ||
          "";
        const cpfFromSubject =
          subjectRef.replace(/^Patient\//, "").split("-")[1] || "";
        await notificationService.sendNotification(
          buildCobrancaConsultaNotification({
            cpf: cpfFromParticipant || cpfFromSubject,
            nomePaciente: props?.appointment?.patientName || "",
            tipoConsulta: props?.appointment?.healthcare || "",
            nomeProfissional: props?.appointment?.practitioner || "",
            linkPagamento: paymentLink,
            organizationId: organization?.id,
          }),
          { organizationId: organization?.id },
        );
      }

      const wasAlreadySent = isChargeNotificationSentOnAppointment(
        props?.appointment?.originalData
      );
      await markAppointmentChargeNotificationSent(
        props?.appointment?.originalData
      );
      if (!wasAlreadySent && !keepOpenAfterCharge) {
        props?.setRefetch?.((prev: any) => !prev);
      }
      setErrorSendCharge(undefined);
      toast.success("Cobrança enviada com sucesso!");
      // Só fecha após enviar cobrança fora do contexto plano gratuito + Flux Pay
      if (!keepOpenAfterCharge) {
        setPaymentModalOpen(false);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setErrorSendCharge(
          "Não é possível enviar cobrança para consulta em agenda sem custo. Confirme a consulta no botão a seguir",
        );
      } else {
        setErrorSendCharge(
          "Erro ao gerar link de pagamento. Por favor, tente novamente.",
        );
      }
      throw error;
    } finally {
      setLoadingSendCharge(false);
    }
  };

  const handleConfirmPayment = async () => {
    setLoadingConfirmPayment(true);
    try {
      await EditAppointment({
        ...props?.appointment?.originalData,
        status: "booked",
      });

      if (payment?.length > 0) {
        const paymentCopy = { ...payment[0]?.resource };
        paymentCopy.paymentStatus = {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/paymentstatus",
              code: "paid",
            },
          ],
        };
        try {
          await PutPaymentNotice({
            paymentId: payment[0]?.resource?.id,
            data: paymentCopy,
          });
        } catch (err) {
          console.error("Erro ao atualizar PaymentNotice:", err);
        }
      }

      props?.setRefetch?.((prev: any) => !prev);
      setPaymentModalOpen(false);

      // Pré-consulta automática após confirmação de pagamento (status booked).
      try {
        const triggerContext = buildPreConsultationTriggerContextFromAppointment(
          {
            organizationId: organization?.id,
            appointment: props?.appointment?.originalData,
            patientDisplayName: props?.appointment?.subject?.display,
            requesterReference: buildScheduleManagerRequesterReference({
              organizationId: organization?.id,
              practitionerUserId: user?.username,
            }),
          },
        );
        if (triggerContext && props?.appointment?.originalData?.id) {
          const triggerInput = buildTriggerInputFromPreConsultationContext(
            props.appointment.originalData.id,
            triggerContext,
          );
          if (triggerInput) {
            await triggerPreConsultationAutomation(triggerInput);
          }
        }
      } catch (automationError) {
        console.error("[automationTrigger] pré-consulta:", automationError);
      }
    } catch {
      // Pode adicionar um toast ou erro se desejar
    } finally {
      setLoadingConfirmPayment(false);
    }
  };

  /**
   * Função para confirmar appointments com status "proposed"
   * Verifica se há PaymentNotice ativo:
   * - Se houver PaymentNotice ativo: muda status para "pending" (pagamento pendente)
   * - Se não houver PaymentNotice: muda status para "booked" (confirmado sem pagamento)
   *
   * @returns Promise<string | undefined> - Retorna o status final ("pending" ou "booked") ou undefined em caso de erro
   */
  const handleConfirmProposedAppointment = async (): Promise<
    string | undefined
  > => {
    if (!props?.appointment?.originalData?.id) {
      toast.error("ID do appointment não encontrado");
      return;
    }

    setLoadingConfirmProposed(true);
    try {
      // Primeiro, busca o PaymentNotice para verificar se há pagamento pendente
      const paymentData = await fetchPayment();

      // Verifica se há PaymentNotice ativo (pagamento pendente)
      // PaymentNotice com status "active" indica pagamento pendente
      const hasActivePaymentNotice =
        paymentData?.length > 0 &&
        paymentData[0]?.resource?.status === "active";

      // Determina o status final baseado na presença de pagamento pendente
      const finalStatus = hasActivePaymentNotice ? "pending" : "booked";

      await patchAppointmentStatusAndTriggerPreConsultation({
        appointmentId: props.appointment.originalData.id,
        status: finalStatus,
        preConsultationTrigger:
          finalStatus === "booked"
            ? buildPreConsultationTriggerContextFromAppointment({
                organizationId: organization?.id,
                appointment: props.appointment.originalData,
                patientDisplayName: props?.appointment?.subject?.display,
                requesterReference: buildScheduleManagerRequesterReference({
                  organizationId: organization?.id,
                  practitionerUserId: user?.username,
                }),
              }) ?? undefined
            : undefined,
      });

      const successMessage = hasActivePaymentNotice
        ? "Consulta confirmada. Aguardando pagamento."
        : "Consulta confirmada com sucesso!";

      toast.success(successMessage);
      props?.setRefetch?.((prev: any) => !prev);

      // Retorna o status final para que o componente pai possa tomar ações adicionais
      return finalStatus;
    } catch (error) {
      console.error("Erro ao confirmar appointment:", error);
      toast.error("Erro ao confirmar consulta. Por favor, tente novamente.");
      return undefined;
    } finally {
      setLoadingConfirmProposed(false);
    }
  };

  return {
    // Estados e hand lers
    setShowPaymentWarning,
    startServiceLoading,
    handleStartService,
    handleAddToCalendar,
    loadingCalendar,
    handleRemoveFromCalendar,
    handleConfirmPayment,
    handleViewRecords,
    setVerifyDialog,
    fetchRelated,
    verifyDialog,
    warningAction,
    showPaymentWarning,
    related,
    fetchPayment,
    payment,
    isPaid,
    paymentModalOpen,
    setPaymentModalOpen,
    loadingSendCharge,
    setLoadingSendCharge,
    errorSendCharge,
    setErrorSendCharge,
    loadingConfirmPayment,
    setLoadingConfirmPayment,
    handleSendCharge,
    loadingConfirmProposed,
    handleConfirmProposedAppointment,
  };
};
